/**
 * Closed-vocabulary primitives shared by every content entity (content-schema §4–6).
 *
 * These are the atoms the engine knows how to interpret: Decimal-safe amount
 * strings, cost/growth curves, effects with a fixed op order, and the unlock
 * condition combinator tree. New primitives are engine additions (with tests),
 * never formula strings in data — that is what keeps content declarative.
 */
import { z } from 'zod';

/**
 * Every numeric amount in content is a **string** so Decimal values survive JSON
 * without float loss (content-schema §3). Accepts integers, decimals and
 * scientific notation (`"1"`, `"1.5"`, `"1.5e12"`, `"-0.2"`).
 */
export const AmountSchema = z
  .string()
  .regex(
    /^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?$/,
    'must be a Decimal-safe numeric string (e.g. "1.5e12")',
  );

/** Entity id: the dotted `kind.age.name` foreign key of the whole game (§1). */
export const IdSchema = z
  .string()
  .regex(
    /^[a-z][a-zA-Z0-9]*(?:\.[a-zA-Z0-9-]+)+$/,
    'must be a dotted id such as "gen.primordial.mitochondria"',
  );

// ── Cost / growth curves (content-schema §4) ──────────────────────────────────
// A closed set. `base`/`value` are Decimal strings; `growth`/`exponent`/`step`
// are ordinary numbers (small, float-safe multipliers/exponents).

export const CurveSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('constant'), value: AmountSchema }).strict(),
  z
    .object({ type: z.literal('linear'), base: AmountSchema, step: AmountSchema })
    .strict(),
  z
    .object({
      type: z.literal('exponential'),
      base: AmountSchema,
      growth: z.number().gt(0),
    })
    .strict(),
  z
    .object({ type: z.literal('polynomial'), base: AmountSchema, exponent: z.number() })
    .strict(),
]);

// ── Effects (content-schema §5) ───────────────────────────────────────────────
// Ops apply in the fixed order add → mul → pow regardless of source order, so
// effect stacking is deterministic.

export const EffectOpSchema = z.enum(['add', 'mul', 'pow']);

/**
 * The effect-target registry: which `<kind>` accepts which `<prop>`, and whether
 * it carries an id referencing an entity of `ref` kind. An effect target that
 * does not parse against this registry is a validation failure (§5).
 */
export const EFFECT_TARGET_REGISTRY = {
  generator: { props: ['rate', 'cost'], ref: 'generator' },
  /** `start` is a keepsake (GAM-11): granted on each new universe after a full-reset prestige. */
  resource: { props: ['passive', 'start'], ref: 'resource' },
  breakthrough: { props: ['chance', 'cost'], ref: 'breakthrough' },
  age: { props: ['allProduction'], ref: 'age' },
  clickAction: { props: ['amount'], ref: 'clickAction' },
  prestige: { props: ['gain'], ref: 'prestige' },
  /** `start` is a keepsake (GAM-11): the trait is owned from the start of each new universe. */
  trait: { props: ['start'], ref: 'trait' },
  /** Production multiplier across every age and universe (GAM-11 Essence tree). */
  global: { props: ['allProduction'], ref: null },
  offline: { props: ['cap'], ref: null },
} as const;

export type EffectTargetKind = keyof typeof EFFECT_TARGET_REGISTRY;
export type EffectRefKind = Exclude<
  (typeof EFFECT_TARGET_REGISTRY)[EffectTargetKind]['ref'],
  null
>;

export interface ParsedEffectTarget {
  readonly kind: EffectTargetKind;
  /** The referenced entity id, or `null` for id-less targets like `offline.cap`. */
  readonly id: string | null;
  readonly prop: string;
  /** Which id-space `id` must resolve against, or `null` when id-less. */
  readonly ref: EffectRefKind | null;
}

/**
 * Parse `<kind>.<id>.<prop>` (ids may themselves contain dots, so the kind is the
 * first segment and the prop is the last). Returns `null` when the target does
 * not match the registry — the caller turns that into a named validation error.
 */
export function parseEffectTarget(target: string): ParsedEffectTarget | null {
  const parts = target.split('.');
  if (parts.length < 2) return null;

  const kind = parts[0];
  if (kind === undefined || !isEffectTargetKind(kind)) return null;

  const entry = EFFECT_TARGET_REGISTRY[kind];
  const prop = parts[parts.length - 1];
  if (prop === undefined || !(entry.props as readonly string[]).includes(prop))
    return null;

  const id = parts.slice(1, -1).join('.');
  const hasId = entry.ref !== null;
  if (hasId && id.length === 0) return null;
  if (!hasId && id.length > 0) return null;

  return { kind, id: hasId ? id : null, prop, ref: entry.ref };
}

function isEffectTargetKind(value: string): value is EffectTargetKind {
  return Object.prototype.hasOwnProperty.call(EFFECT_TARGET_REGISTRY, value);
}

export const EffectSchema = z
  .object({
    target: z.string().refine((t) => parseEffectTarget(t) !== null, {
      message:
        'unknown effect target (see the effect-target registry, content-schema §5)',
    }),
    op: EffectOpSchema,
    value: AmountSchema,
    /** What Glorp says the first time this specific effect is granted — for entities
     * that stack several effects and want a line per effect rather than per entity.
     * The engine never reads it; safe to omit. */
    narration: z.string().optional(),
  })
  .strict();

// ── Unlock conditions (content-schema §6) ─────────────────────────────────────
// A combinator tree: all / any / not over a fixed set of leaf predicates.

export type LeafCondition =
  | { type: 'resourceLifetime'; resource: string; amount: string }
  | { type: 'resourceCurrent'; resource: string; amount: string }
  | { type: 'generatorOwned'; generator: string; count: number }
  | { type: 'upgradeOwned'; upgrade: string }
  | { type: 'traitOwned'; trait: string }
  | { type: 'milestone'; milestone: string }
  | { type: 'breakthroughHits'; breakthrough: string; hits: number }
  | { type: 'ageCompleted'; age: string }
  | { type: 'prestigeCount'; layer: string; count: number };

export type Condition =
  LeafCondition | { all: Condition[] } | { any: Condition[] } | { not: Condition };

const LeafConditionSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('resourceLifetime'),
      resource: IdSchema,
      amount: AmountSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('resourceCurrent'),
      resource: IdSchema,
      amount: AmountSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('generatorOwned'),
      generator: IdSchema,
      count: z.number().int().nonnegative(),
    })
    .strict(),
  z.object({ type: z.literal('upgradeOwned'), upgrade: IdSchema }).strict(),
  z.object({ type: z.literal('traitOwned'), trait: IdSchema }).strict(),
  z.object({ type: z.literal('milestone'), milestone: IdSchema }).strict(),
  z
    .object({
      type: z.literal('breakthroughHits'),
      breakthrough: IdSchema,
      hits: z.number().int().positive(),
    })
    .strict(),
  z.object({ type: z.literal('ageCompleted'), age: IdSchema }).strict(),
  z
    .object({
      type: z.literal('prestigeCount'),
      layer: IdSchema,
      count: z.number().int().nonnegative(),
    })
    .strict(),
]);

export const ConditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    LeafConditionSchema,
    z.object({ all: z.array(ConditionSchema) }).strict(),
    z.object({ any: z.array(ConditionSchema) }).strict(),
    z.object({ not: ConditionSchema }).strict(),
  ]),
);

export type Amount = z.infer<typeof AmountSchema>;
export type Curve = z.infer<typeof CurveSchema>;
export type Effect = z.infer<typeof EffectSchema>;
export type EffectOp = z.infer<typeof EffectOpSchema>;
